import { useState, type ReactNode } from "react";

export type CalcSheetTone = "pass" | "near" | "fail" | "idle";

export type CalcSheetScore = {
  key: string;
  label: string;
  value: number | null;
};

export type CalcSheetMetric = {
  key: string;
  label: string;
  value: ReactNode;
  tone?: CalcSheetTone;
  mono?: boolean;
};

export type CalcSheetNotice = {
  key: string;
  message: ReactNode;
  tone?: "note" | "warn" | "fail";
  reference?: ReactNode;
};

export function calcSheetTone(value: number | null): CalcSheetTone {
  if (value == null) return "idle";
  if (value > 1) return "fail";
  if (value >= 0.9) return "near";
  return "pass";
}

function scorePercent(value: number | null): string {
  return value == null ? "—" : `${Math.round(value * 100)}%`;
}

// Typeset engine symbol strings at render: "Δ_allow" → Δ<sub>allow</sub>,
// "V_max" → V<sub>max</sub>. Underscore subscripts never reach the reader raw;
// the engine/model strings themselves stay untouched.
export function symbolText(symbol: string): ReactNode {
  if (!symbol.includes("_")) return symbol;
  return symbol.split(/(\s+)/).map((token, index) => {
    const cut = token.indexOf("_");
    if (cut <= 0 || cut === token.length - 1) return token;
    return (
      <span key={index}>
        {token.slice(0, cut)}
        <sub>{token.slice(cut + 1)}</sub>
      </span>
    );
  });
}

// Headerstrip D/C chips: cap the visible row and fold the rest into one "+n"
// chip (title lists them) so a many-check calculator never pushes the strip
// into wrapping/truncation. The mobile score grid still lists every check.
const MAX_VISIBLE_SCORE_CHIPS = 6;

export function splitScoreOverflow<T>(items: readonly T[]): {
  visible: readonly T[];
  overflow: readonly T[];
} {
  if (items.length <= MAX_VISIBLE_SCORE_CHIPS) return { visible: items, overflow: [] };
  const visible = items.slice(0, MAX_VISIBLE_SCORE_CHIPS - 1);
  return { visible, overflow: items.slice(MAX_VISIBLE_SCORE_CHIPS - 1) };
}

export function CalcSheetHeader({
  mark,
  onMark,
  onMarkCommit,
  calculator,
  calculatorEyebrow,
  calculatorControl,
  calculatorHelp,
  standard,
  scores = [],
  status,
  statusTone = "idle",
  statusEyebrow = "Status",
  note,
  actions,
  readOnly = false,
}: {
  mark: string;
  onMark?: (value: string) => void;
  onMarkCommit?: () => void;
  calculator: string;
  /** Eyebrow over the calculator block (defaults to "Calculator"). */
  calculatorEyebrow?: string;
  /** Master control replacing the static calculator label (e.g. the connection
      sheet's fastening select) — same block, same grammar as the member
      sheet's role select. */
  calculatorControl?: ReactNode;
  /** Optional contextual help composed beside the calculator block. */
  calculatorHelp?: ReactNode;
  standard?: string | null;
  scores?: readonly CalcSheetScore[];
  status: string;
  statusTone?: CalcSheetTone;
  statusEyebrow?: string;
  note?: string | null;
  actions?: ReactNode;
  readOnly?: boolean;
}) {
  return (
    <div className="nds-headerstrip calc-sheet-headerstrip">
      <label className="nds-hs-block">
        <span className="nds-hs-eyebrow">Mark</span>
        <input
          className="nds-hs-mark"
          value={mark}
          readOnly={readOnly || !onMark}
          onChange={(event) => onMark?.(event.target.value)}
          onBlur={onMarkCommit}
          onKeyDown={(event) => {
            if (event.key === "Enter") onMarkCommit?.();
          }}
          aria-label="Calculation name / mark"
        />
      </label>

      <div className="nds-hs-block nds-hs-roleblock calc-sheet-typeblock">
        <span className="nds-hs-eyebrow">{calculatorEyebrow ?? "Calculator"}</span>
        {calculatorControl ?? <strong className="calc-sheet-type">{calculator}</strong>}
      </div>
      {calculatorHelp}

      {standard ? <div className="nds-hs-preset">{standard}</div> : null}
      {note ? <div className="calc-sheet-header-note">{note}</div> : null}

      {scores.length > 0 ? (() => {
        const { visible, overflow } = splitScoreOverflow(scores);
        return (
          <>
            <div className="nds-hs-chips" aria-label="Calculation utilization scores">
              {visible.map((score) => (
                <span className={`nds-dcrchip is-${calcSheetTone(score.value)}`} key={score.key}>
                  <span className="nds-dcrchip-dot" aria-hidden="true" />
                  <span className="nds-dcrchip-label">{score.label}</span>
                  <span className="num nds-dcrchip-pct">{scorePercent(score.value)}</span>
                </span>
              ))}
              {overflow.length > 0 ? (
                <span
                  className="nds-dcrchip is-idle nds-dcrchip-more"
                  title={overflow.map((score) => `${score.label} ${scorePercent(score.value)}`).join(" · ")}
                >
                  <span className="num nds-dcrchip-pct">+{overflow.length}</span>
                </span>
              ) : null}
            </div>
            <details className="nds-hs-scoremobile">
              <summary>
                <span>{scores.length} checks</span>
                <span className="num">{status}</span>
              </summary>
              <div className="nds-hs-scoregrid">
                {scores.map((score) => (
                  <span className={`nds-dcrchip is-${calcSheetTone(score.value)}`} key={score.key}>
                    <span className="nds-dcrchip-dot" aria-hidden="true" />
                    <span className="nds-dcrchip-label">{score.label}</span>
                    <span className="num nds-dcrchip-pct">{scorePercent(score.value)}</span>
                  </span>
                ))}
              </div>
            </details>
          </>
        );
      })() : null}

      <div className={`nds-statuspill is-${statusTone}`} role="status" aria-live="polite">
        <span className="nds-statuspill-dot" aria-hidden="true" />
        <span className="nds-statuspill-eyebrow">{statusEyebrow}</span>
        <span className="nds-statuspill-label">{status}</span>
      </div>
      {actions ? <div className="nds-hs-actions">{actions}</div> : null}
    </div>
  );
}

export function CalcInputCard({
  letter,
  title,
  hint,
  actions,
  stickyHeader = false,
  open,
  defaultOpen = false,
  onToggle,
  children,
  className = "",
}: {
  letter: string;
  title: ReactNode;
  hint?: string | null;
  actions?: ReactNode;
  stickyHeader?: boolean;
  open?: boolean;
  defaultOpen?: boolean;
  onToggle?: () => void;
  children: ReactNode;
  className?: string;
}) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isOpen = open ?? internalOpen;
  const toggle = onToggle ?? (() => setInternalOpen((current) => !current));

  return (
    <section className={`nds-card${stickyHeader ? " is-sticky" : ""}${className ? ` ${className}` : ""}`}>
      <div className={`nds-card-head${isOpen ? " is-open" : ""}`}>
        <button
          type="button"
          className="nds-card-toggle"
          onClick={toggle}
          aria-expanded={isOpen}
        >
          <span className="nds-card-letter">{letter}</span>
          <span className="nds-card-title">{title}</span>
          {hint ? <span className="nds-card-hint">{hint}</span> : null}
          <span className={`nds-card-chev${isOpen ? " is-open" : ""}`} aria-hidden="true">⌄</span>
        </button>
        {actions ? <div className="nds-card-actions">{actions}</div> : null}
      </div>
      {isOpen ? <div className="nds-card-body">{children}</div> : null}
    </section>
  );
}

export function CalcResultPanel({
  title,
  hint,
  actions,
  children,
  className = "",
  ariaLabel,
}: {
  title: ReactNode;
  hint?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  ariaLabel?: string;
}) {
  return (
    <section
      className={`calc-sheet-panel${className ? ` ${className}` : ""}`}
      aria-label={ariaLabel}
    >
      <header className="calc-sheet-panel-head">
        <div className="calc-sheet-panel-heading">
          <strong>{title}</strong>
          {hint ? <span>{hint}</span> : null}
        </div>
        {actions ? <div className="calc-sheet-panel-actions">{actions}</div> : null}
      </header>
      {children}
    </section>
  );
}

export function CalcMetricStrip({ items }: { items: readonly CalcSheetMetric[] }) {
  return (
    <div className={`calc-sheet-metrics has-${Math.min(items.length, 6)}`}>
      {items.map((item) => (
        <div className={`calc-sheet-metric is-${item.tone ?? "idle"}`} key={item.key}>
          <span>{item.label}</span>
          <strong className={item.mono ? "num" : undefined}>{item.value}</strong>
        </div>
      ))}
    </div>
  );
}

export function CalcNoticeList({ items }: { items: readonly CalcSheetNotice[] }) {
  return (
    <div className="nds-warns calc-sheet-notices">
      {items.map((item) => (
        <div className={`nds-warnrow is-${item.tone ?? "note"}`} key={item.key}>
          <span className="nds-warnrow-dot" aria-hidden="true" />
          <span className="nds-warnrow-msg">{item.message}</span>
          {item.reference ? <span className="num nds-warnrow-ref">{item.reference}</span> : null}
        </div>
      ))}
    </div>
  );
}

export function CalcCheckCard({
  label,
  dcr,
  demand,
  capacity,
  equation,
  reference,
  tone,
  helpshotId,
}: {
  label: ReactNode;
  dcr: number | null;
  demand?: ReactNode;
  capacity?: ReactNode;
  equation?: ReactNode;
  reference?: ReactNode;
  tone?: CalcSheetTone;
  helpshotId?: string;
}) {
  const resolvedTone = tone ?? calcSheetTone(dcr);
  const barWidth = dcr == null ? 0 : Math.min(100, Math.max(0, Math.round(dcr * 100)));
  const verdict = resolvedTone === "pass"
    ? "PASS"
    : resolvedTone === "near"
      ? "NEAR"
      : resolvedTone === "fail" ? "FAIL" : "—";

  return (
    <div className={`nds-checkcard is-${resolvedTone}`} data-helpshot={helpshotId}>
      <div className="nds-checkcard-head">
        <span className="nds-checkcard-dot" aria-hidden="true" />
        <span className="nds-checkcard-name">{label}</span>
        <span className="num nds-checkcard-dc">{scorePercent(dcr)}</span>
        <span className={`nds-verdicttag is-${resolvedTone}`} aria-label={verdict}>{verdict}</span>
      </div>
      <div className="nds-checkcard-bar" aria-hidden="true">
        <div className="nds-checkcard-bar-fill" style={{ width: `${barWidth}%` }} />
      </div>
      <div className="nds-checkcard-body">
        {demand != null || capacity != null ? (
          <div className="num">{demand ?? "—"} / {capacity ?? "—"}</div>
        ) : null}
        {equation ? <div className="nds-checkcard-eq">{equation}</div> : null}
        {reference ? <div className="nds-checkcard-ref">{reference}</div> : null}
      </div>
    </div>
  );
}

export function CalcGoverningBanner({
  label,
  tone,
  dcr,
  governing,
  meta,
}: {
  label: string;
  tone: CalcSheetTone;
  dcr: number | null;
  governing?: ReactNode;
  meta?: ReactNode;
}) {
  return (
    <div className={`nds-govbanner is-${tone}`} role="status" aria-live="polite">
      <div className="nds-govbanner-dc">
        <span className="num nds-govbanner-dc-val">{scorePercent(dcr)}</span>
        <span className="nds-govbanner-dc-cap">D/C</span>
      </div>
      <div className="nds-govbanner-mid">
        <div className="nds-govbanner-eyebrow">Overall result</div>
        <div className="nds-govbanner-verdict">{label}</div>
        {governing ? <div className="nds-govbanner-governing">{governing}</div> : null}
      </div>
      {meta ? <div className="nds-govbanner-meta">{meta}</div> : null}
    </div>
  );
}

export function CalcResultGate({
  title,
  message,
  kind = "incomplete",
  reasons = [],
}: {
  title: string;
  message: ReactNode;
  kind?: "empty" | "incomplete" | "pending" | "error";
  reasons?: readonly ReactNode[];
}) {
  const icon = kind === "error" ? "!" : kind === "pending" ? "…" : "⌖";
  return (
    <div className={`nds-gate is-${kind}`}>
      <div className="nds-gate-icon" aria-hidden="true">{icon}</div>
      <div className="nds-gate-title">{title}</div>
      <div className="nds-gate-msg">{message}</div>
      {reasons.length > 0 ? (
        <div className="nds-chiprow nds-gate-chips">
          {reasons.map((reason, index) => (
            <span className="nds-chip is-need" key={index}>{reason}</span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function computedLabel(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const timestamp = new Date(iso);
  if (Number.isNaN(timestamp.getTime())) return null;
  return `computed ${timestamp.toLocaleDateString()} ${timestamp.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
}

export function CalcResultMeta({
  source,
  engine,
  standard,
  computedAt,
  method,
}: {
  source: ReactNode;
  engine?: ReactNode;
  standard?: ReactNode;
  computedAt?: string | null;
  method?: ReactNode;
}) {
  const computed = computedLabel(computedAt);
  return (
    <div className="nds-result-meta">
      <span className="nds-prov-source">{source}</span>
      {engine ? <span className="num nds-prov-engine">{engine}</span> : null}
      {standard ? <span className="num nds-prov-edition">{standard}</span> : null}
      {computed ? <span className="num nds-prov-time">{computed}</span> : null}
      {method ? <span className="num nds-prov-method">{method}</span> : null}
    </div>
  );
}

export function CalcDisclosure({
  title,
  hint,
  count,
  defaultOpen = false,
  children,
  className = "",
}: {
  title: ReactNode;
  hint?: ReactNode;
  count?: number;
  defaultOpen?: boolean;
  children: ReactNode;
  className?: string;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <details
      className={`nds-acc calc-sheet-disclosure${className ? ` ${className}` : ""}`}
      open={isOpen}
      onToggle={(event) => {
        if (event.currentTarget.open !== isOpen) setIsOpen(event.currentTarget.open);
      }}
    >
      <summary className="nds-acc-head">
        <span className="nds-acc-title">{title}</span>
        {hint ? <span className="nds-acc-hint">{hint}</span> : null}
        {count != null ? <span className="calc-sheet-disclosure-count num">{count}</span> : null}
      </summary>
      <div className="nds-acc-body">{children}</div>
    </details>
  );
}
